import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useVideoProtection } from "@/hooks/useVideoProtection";
import { ForensicWatermark } from "@/components/lesson/ForensicWatermark";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import logo from "@/assets/logo.png";
import {
  ArrowLeft, Lock, CheckCircle, PlayCircle, ClipboardList,
  Loader2, Home, LogOut, ChevronRight, Award, PanelLeftClose, PanelLeft, ShieldAlert,
} from "lucide-react";
import LessonExtras from "@/components/lesson/LessonExtras";
import { FullscreenButton } from "@/components/lesson/FullscreenButton";
import { LegalWarningModal } from "@/components/lesson/LegalWarningModal";
import { useSecureVideoStream } from "@/hooks/useSecureVideoStream";

const translations = {
  en: {
    lessons: "Lessons", watchVideo: "Watch the entire video to unlock the exam",
    takeExam: "Take Exam", lessonCompleted: "Lesson completed!", courseComplete: "🎉 Course Complete!",
    congratulations: "Congratulations", completedAll: "You've completed all lessons and passed every exam.",
    requestCertificate: "Request Certificate", certificateRequested: "✅ Certificate request submitted! Pending admin approval.",
    certificateApproved: "✅ Certificate approved and sent to your email!",
    certificatePending: "⏳ Certificate request pending admin approval.",
    backToVideo: "Back to Video", submitExam: "Submit Exam", nextLesson: "Next Lesson",
    tryAgain: "Try Again", youPassed: "🎉 You passed!", passingScore: "Passing score",
    questions: "questions", lessonExam: "Lesson Exam", score: "Score", dashboard: "Dashboard",
    noContent: "This lesson has no content yet. Check back soon!",
    selectLesson: "Select a lesson from the sidebar to begin.",
    inProgress: "In progress", videoWatched: "Video watched", completed: "Completed",
    home: "Home", showLessons: "Show Lessons",
    correctAnswers: "Correct Answers", notQuite: "Not quite!",
    watchOrder: "Please watch the video in order", fastForwardDisabled: "Fast-forwarding is disabled.",
    certRejected: "Certificate request was rejected.", certRequestedTitle: "Already Requested",
    certRequestedDesc: "You have already requested a certificate. Contact Fitawrari Support on Telegram for help.",
    securityWarning: "⚠️ Unauthorized recording or downloading will result in immediate loss of access.",
    error: "An error occurred.",
  },
  am: {
    lessons: "ትምህርቶች", watchVideo: "ፈተናውን ለመክፈት ሙሉ ቪዲዮውን ይመልከቱ",
    takeExam: "ፈተና ይውሰዱ", lessonCompleted: "ትምህርት ተጠናቋል!", courseComplete: "🎉 ኮርስ ተጠናቋል!",
    congratulations: "እንኳን ደስ አለዎት", completedAll: "ሁሉንም ትምህርቶች አጠናቅቀዋል።",
    requestCertificate: "ሰርተፊኬት ይጠይቁ", certificateRequested: "✅ የሰርተፊኬት ጥያቄ ቀርቧል!",
    certificateApproved: "✅ ሰርተፊኬት ጸድቆ ወደ ኢሜልዎ ተልኳል!",
    certificatePending: "⏳ የሰርተፊኬት ጥያቄ በመጠባበቅ ላይ።",
    backToVideo: "ወደ ቪዲዮ ተመለስ", submitExam: "ፈተና አስገባ", nextLesson: "ቀጣይ ትምህርት",
    tryAgain: "እንደገና ሞክር", youPassed: "🎉 አልፈዋል!", passingScore: "የማለፊያ ነጥብ",
    questions: "ጥያቄዎች", lessonExam: "የትምህርት ፈተና", score: "ውጤት", dashboard: "ዳሽቦርድ",
    noContent: "ይህ ትምህርት ገና ይዘት የለውም።",
    selectLesson: "ለመጀመር ትምህርት ይምረጡ።",
    inProgress: "በሂደት ላይ", videoWatched: "ቪዲዮ ታይቷል", completed: "ተጠናቋል",
    home: "መነሻ", showLessons: "ትምህርቶች አሳይ",
    correctAnswers: "ትክክለኛ መልሶች", notQuite: "ትንሽ ይቀራል!",
    watchOrder: "እባክዎን ቪዲዮውን በቅደም ተከተል ይመልከቱ", fastForwardDisabled: "ወደ ፊት ማሳለፍ አይቻልም።",
    certRejected: "የሰርተፊኬት ጥያቄው ተቀባይነት አላገኘም።", certRequestedTitle: "ቀድሞ ተጠይቋል",
    certRequestedDesc: "ለዚህ ኮርስ ቀድሞ ሰርተፊኬት ጠይቀዋል፤ ለተጨማሪ መረጃ ቴሌግራም ላይ Fitawrari Support ያግኙ።",
    securityWarning: "⚠️ ያልተፈቀደ ቀረጻ ወይም ማውረድ ወዲያውኑ የኮርስ መዳረሻን ያሳጣል።",
    error: "ስህተት ተከስቷል።",
  },
};

type Lesson = { id: string; title: string; description: string | null; video_url: string | null; sort_order: number; passing_score: number; title_am?: string; description_am?: string };
type Question = { id: string; question_text: string; options: string[]; correct_option_index: number; sort_order: number; question_text_am?: string; options_am?: string[] };
type LessonProgress = { lesson_id: string; video_completed: boolean; exam_passed: boolean; best_score: number; video_watch_time: number };

type CourseViewProps = {
  courseId?: string;
  initialLessonId?: string;
  onBack?: () => void;
};

const CourseView = ({ courseId: propCourseId, initialLessonId, onBack }: CourseViewProps) => {
  const { courseId: paramCourseId } = useParams<{ courseId: string }>();
  const courseId = propCourseId || paramCourseId;
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { toast } = useToast();

  const [lang, setLang] = useState<"en" | "am">(() => {
    return (localStorage.getItem("fitawrari_lang") as "en" | "am") || "en";
  });

  const toggleLang = () => {
    const newLang = lang === "en" ? "am" : "en";
    setLang(newLang);
    localStorage.setItem("fitawrari_lang", newLang);
  };
  const t = translations[lang];

  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [progress, setProgress] = useState<LessonProgress[]>([]);
  const [activeLesson, setActiveLesson] = useState<Lesson | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [showExam, setShowExam] = useState(false);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [examResult, setExamResult] = useState<{ score: number; passed: boolean } | null>(null);
  const [submittingExam, setSubmittingExam] = useState(false);
  const [courseTitle, setCourseTitle] = useState("");
  const [requestingCert, setRequestingCert] = useState(false);
  const [certStatus, setCertStatus] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [securitySettings, setSecuritySettings] = useState<Record<string, unknown>>({});

  const videoRef = useRef<HTMLVideoElement>(null);
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const watchedTimeRef = useRef(0);
  const maxTimeRef = useRef(0);
  const [videoCompleted, setVideoCompleted] = useState(false);
  const [userPhone, setUserPhone] = useState<string>("");

  // Fetch course-level security_settings (admin-controlled per course)
  useEffect(() => {
    if (!courseId) return;
    supabase.from("courses").select("security_settings, title").eq("id", courseId).maybeSingle()
      .then(({ data }) => {
        if (data?.security_settings) setSecuritySettings(data.security_settings as Record<string, unknown>);
        if (data?.title) setCourseTitle(data.title);
      });
  }, [courseId]);

  // Auth-gated chunked stream → blob URL via MediaSource. Direct file URL is never exposed.
  const { blobUrl: blobVideoUrl, error: streamError } = useSecureVideoStream({
    source: "course",
    resourceId: courseId,
    lessonId: activeLesson?.id,
    enabled: !!activeLesson?.video_url,
  });

  // Fetch student phone for forensic watermark
  useEffect(() => {
    if (!user?.id) return;
    supabase.from("profiles").select("phone").eq("user_id", user.id).maybeSingle()
      .then(({ data }) => { if (data?.phone) setUserPhone(data.phone); });
  }, [user?.id]);

  // Video protection system - auto-revoke signs out and redirects
  const handleAccessRevoked = useCallback(async () => {
    await signOut();
    navigate("/");
  }, [navigate, signOut]);

  const { canvasRef } = useVideoProtection({
    userEmail: user?.email || "unknown",
    userId: user?.id || "unknown",
    videoRef: videoRef as React.RefObject<HTMLVideoElement>,
    containerRef: videoContainerRef as React.RefObject<HTMLDivElement>,
    enabled: !!blobVideoUrl && !!activeLesson?.video_url,
    onAccessRevoked: handleAccessRevoked,
    securitySettings: securitySettings as never,
  });

  // Surface stream errors to the user
  useEffect(() => {
    if (streamError) {
      toast({ title: "Video unavailable", description: streamError, variant: "destructive" });
    }
  }, [streamError, toast]);

  const progressMap = new Map(progress.map((p) => [p.lesson_id, p]));

  const isLessonUnlocked = useCallback(
    (lesson: Lesson, index: number) => {
      if (index === 0) return true;
      const prevLesson = lessons[index - 1];
      const prevProg = progressMap.get(prevLesson.id);
      return prevProg?.exam_passed === true;
    },
    [lessons, progress]
  );

  useEffect(() => {
    const fetchCourse = async () => {
      if (!courseId) return;
      const [courseRes, lessonsRes, progressRes] = await Promise.all([
        supabase.from("courses").select("title").eq("id", courseId).single(),
        supabase.from("lessons").select("*").eq("course_id", courseId).order("sort_order"),
        supabase.from("student_lesson_progress").select("*"),
      ]);
      setCourseTitle(courseRes.data?.title || "Course");
      const lessonData = (lessonsRes.data || []) as Lesson[];
      setLessons(lessonData);
      setProgress((progressRes.data || []) as LessonProgress[]);
      if (lessonData.length > 0) {
        const progressSet = new Map((progressRes.data || []).map((p: any) => [p.lesson_id, p]));
        // Prefer deep-linked lesson if provided
        const deep = initialLessonId ? lessonData.find((l) => l.id === initialLessonId) : null;
        const firstIncomplete = lessonData.find((l, i) => {
          const p = progressSet.get(l.id);
          const unlocked = i === 0 || progressSet.get(lessonData[i - 1].id)?.exam_passed;
          return unlocked && !p?.exam_passed;
        });
        setActiveLesson(deep || firstIncomplete || lessonData[0]);
      }
      setLoading(false);
    };
    fetchCourse();
  }, [courseId]);

  useEffect(() => {
    if (!activeLesson) return;
    setShowExam(false); setExamResult(null); setAnswers({});
    setVideoCompleted(false); watchedTimeRef.current = 0; maxTimeRef.current = 0;
    const prog = progressMap.get(activeLesson.id);
    if (prog?.video_completed) setVideoCompleted(true);
    supabase.from("lesson_questions_student" as any).select("id, question_text, options, sort_order, question_text_am, options_am").eq("lesson_id", activeLesson.id).order("sort_order")
      .then(({ data }) => setQuestions((data || []).map((q: any) => ({ ...q, correct_option_index: -1 })) as Question[]));
    // Video is loaded by useSecureVideoStream — no signed URL fetch needed
  }, [activeLesson?.id]);

  const handleTimeUpdate = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.currentTime > maxTimeRef.current + 2) {
      video.currentTime = maxTimeRef.current;
      toast({ title: t.watchOrder, description: t.fastForwardDisabled, variant: "destructive" });
      return;
    }
    if (video.currentTime > maxTimeRef.current) maxTimeRef.current = video.currentTime;
    watchedTimeRef.current = video.currentTime;
  };

  const handleVideoEnded = async () => {
    if (!activeLesson || !user) return;
    setVideoCompleted(true);
    const existing = progressMap.get(activeLesson.id);
    try {
      await supabase.functions.invoke("manage-certificates", {
        body: { action: "update_video_progress", lessonId: activeLesson.id, watchTime: maxTimeRef.current },
      });
    } catch (e) { /* silent */ }
    setProgress((prev) => {
      const updated = prev.filter((p) => p.lesson_id !== activeLesson.id);
      updated.push({
        lesson_id: activeLesson.id, video_completed: true,
        exam_passed: existing?.exam_passed || false, best_score: existing?.best_score || 0,
        video_watch_time: Math.round(maxTimeRef.current),
      });
      return updated;
    });
  };

  const handleSeeking = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.currentTime > maxTimeRef.current + 2) video.currentTime = maxTimeRef.current;
  };

  const handleSubmitExam = async () => {
    if (!activeLesson || !user) return;
    setSubmittingExam(true);
    try {
      const res = await supabase.functions.invoke("manage-certificates", {
        body: { action: "submit_exam", lessonId: activeLesson.id, answers },
      });
      if (res.error || !res.data?.success) throw new Error(res.data?.error || "Failed to submit exam");

      const { score, passed, best_score: newBest, correctAnswers } = res.data;
      const existing = progressMap.get(activeLesson.id);

      // If passed, update questions with server-provided correct answers
      if (passed && correctAnswers) {
        setQuestions(prev => prev.map(q => {
          const ca = correctAnswers.find((a: any) => a.id === q.id);
          return ca ? { ...q, correct_option_index: ca.correct_option_index } : q;
        }));
      }

      setProgress((prev) => {
        const updated = prev.filter((p) => p.lesson_id !== activeLesson.id);
        updated.push({
          lesson_id: activeLesson.id, video_completed: true,
          exam_passed: passed || existing?.exam_passed || false, best_score: newBest,
          video_watch_time: existing?.video_watch_time || 0,
        });
        return updated;
      });
      setExamResult({ score, passed });
      if (passed) toast({ title: t.youPassed, description: `${t.score}: ${score}%` });
      else toast({ title: t.notQuite, description: `${score}%. ${t.passingScore}: ${activeLesson.passing_score}%`, variant: "destructive" });
    } catch (err) {
      const error = err as Error;
      toast({ title: t.error, description: err.message, variant: "destructive" });
    } finally {
      setSubmittingExam(false);
    }
  };

  const handleRequestCertificate = async () => {
    if (!user || !courseId) return;
    setRequestingCert(true);
    try {
      const fullName = user.user_metadata?.full_name || "Student";
      const res = await supabase.functions.invoke("manage-certificates", {
        body: { action: "request_certificate", courseId, fullName },
      });
      if (res.error || !res.data?.success) throw new Error(res.data?.error || "Failed");
      if (res.data.alreadyRequested) {
        setCertStatus(res.data.status);
        toast({
          title: t.certRequestedTitle,
          description: t.certRequestedDesc,
        });
      } else {
        setCertStatus("pending");
        toast({ title: t.certificateRequested, description: t.certificatePending });
      }
    } catch (err) {
      const error = err as Error;
      toast({ title: t.error, description: err.message, variant: "destructive" });
    } finally { setRequestingCert(false); }
  };

  const handleBack = () => {
    if (onBack) onBack();
    else navigate("/dashboard");
  };

  const getLessonTitle = (l: Lesson) => lang === "am" && (l as any).title_am ? (l as any).title_am : l.title;
  const getLessonDesc = (l: Lesson) => lang === "am" && (l as any).description_am ? (l as any).description_am : l.description;
  const getQText = (q: Question) => lang === "am" && (q as any).question_text_am ? (q as any).question_text_am : q.question_text;
  const getQOpts = (q: Question) => {
    const am = (q as any).options_am;
    return lang === "am" && Array.isArray(am) && am.length > 0 ? am : q.options;
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-background"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  return (
    <div className="min-h-screen bg-background">
      <LegalWarningModal
        scopeKey={`course_${courseId}`}
        enabled={(securitySettings as any)?.legal_warning_enabled !== false}
      />
      <nav className="sticky top-0 z-50 bg-background border-b border-border">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <a href="/" className="flex items-center gap-2"><img src={logo} alt="Fitawrari FX" className="h-8 w-auto" /></a>
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-medium text-foreground truncate max-w-[200px]">{courseTitle}</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={toggleLang}>
              {lang === "en" ? "አማ" : "EN"}
            </Button>
            <Button variant="ghost" size="sm" onClick={handleBack}>
              <ArrowLeft className="w-4 h-4 mr-1" /> {t.dashboard}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => navigate("/")}><Home className="w-4 h-4" /></Button>
            <Button variant="ghost" size="sm" onClick={signOut}><LogOut className="w-4 h-4" /></Button>
          </div>
        </div>
      </nav>

      <div className="flex flex-col lg:flex-row">
        {sidebarOpen && (
          <aside className="lg:w-80 border-b lg:border-b-0 lg:border-r border-border bg-muted/30 lg:sticky lg:top-[57px] lg:max-h-[calc(100vh-57px)] overflow-y-auto flex-shrink-0">
            <div className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-display font-semibold text-sm text-muted-foreground uppercase tracking-wider">{t.lessons}</h2>
                <Button variant="ghost" size="sm" onClick={() => setSidebarOpen(false)} className="hidden lg:flex">
                  <PanelLeftClose className="w-4 h-4" />
                </Button>
              </div>
              <div className="space-y-1">
                {lessons.map((lesson, index) => {
                  const unlocked = isLessonUnlocked(lesson, index);
                  const prog = progressMap.get(lesson.id);
                  const isActive = activeLesson?.id === lesson.id;
                  return (
                    <button key={lesson.id}
                      className={`w-full text-left px-3 py-3 rounded-lg transition-colors flex items-center gap-3 ${
                        isActive ? "bg-primary/10 text-primary" : unlocked ? "hover:bg-muted text-foreground" : "text-muted-foreground cursor-not-allowed opacity-50"
                      }`}
                      onClick={() => unlocked && setActiveLesson(lesson)} disabled={!unlocked}>
                      <div className="flex-shrink-0">
                        {prog?.exam_passed ? <CheckCircle className="w-5 h-5 text-green-500" /> :
                          !unlocked ? <Lock className="w-5 h-5" /> : <PlayCircle className="w-5 h-5" />}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{index + 1}. {getLessonTitle(lesson)}</p>
                        {prog && <p className="text-xs text-muted-foreground">
                          {prog.exam_passed ? t.completed : prog.video_completed ? t.videoWatched : t.inProgress}
                        </p>}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </aside>
        )}

        <main className="flex-1 p-4 lg:p-8 max-w-4xl">
          {!sidebarOpen && (
            <Button variant="ghost" size="sm" onClick={() => setSidebarOpen(true)} className="mb-4 hidden lg:flex">
              <PanelLeft className="w-4 h-4 mr-1" /> {t.showLessons}
            </Button>
          )}

          {activeLesson ? (
            <div className="space-y-6">
              <div>
                <h1 className="font-display text-2xl font-bold mb-2">{getLessonTitle(activeLesson)}</h1>
                {getLessonDesc(activeLesson) && <p className="text-muted-foreground">{getLessonDesc(activeLesson)}</p>}
              </div>

              {!showExam && activeLesson.video_url && (
                <div className="space-y-3">
                  <div className="bg-destructive/10 border border-destructive/20 rounded-md px-4 py-3 text-destructive text-sm">
                    <div className="flex items-center gap-2 font-semibold mb-2">
                      <ShieldAlert className="w-4 h-4 flex-shrink-0" />
                      {t.securityWarning}
                    </div>
                    <ul className="list-disc list-inside text-xs space-y-1 opacity-90">
                      <li>Screen recording, screenshots (PrintScreen), or screen sharing</li>
                      <li>Opening Developer Tools (F12, Inspect, View Source)</li>
                      <li>Using download tools, IDM, or browser extensions to capture video</li>
                      <li>Switching tabs / minimizing the window while a lesson is playing</li>
                      <li>Logging in from another device while watching</li>
                      <li>Running the course inside a Virtual Machine or automated browser</li>
                    </ul>
                    <p className="text-xs mt-2 font-medium">→ Any of the above will immediately revoke your course access.</p>
                  </div>
                  <Card className="overflow-hidden">
                    <div ref={videoContainerRef} className="relative select-none" onContextMenu={(e) => e.preventDefault()}>
                      {blobVideoUrl ? (
                        <video ref={videoRef} src={blobVideoUrl} className="w-full aspect-video bg-black"
                          controls controlsList="nodownload noremoteplayback nofullscreen" disablePictureInPicture
                          playsInline
                          onContextMenu={(e) => e.preventDefault()} onTimeUpdate={handleTimeUpdate}
                          onEnded={handleVideoEnded} onSeeking={handleSeeking}
                          onDragStart={(e) => e.preventDefault()}
                          style={{ pointerEvents: "auto" }}
                        />
                      ) : (
                        <div className="w-full aspect-video bg-black flex items-center justify-center">
                          <Loader2 className="w-8 h-8 animate-spin text-white" />
                        </div>
                      )}
                      {/* Right-click protection overlay - pointer-events-none lets clicks reach video controls */}
                      <div 
                        className="absolute inset-0 z-20 bg-transparent select-none pointer-events-none"
                        style={{ WebkitUserSelect: "none", MozUserSelect: "none", msUserSelect: "none", userSelect: "none" }}
                      />
                      {/* Watermark canvas overlay */}
                      <canvas
                        ref={canvasRef}
                        className="absolute inset-0 w-full h-full pointer-events-none z-10"
                        style={{ mixBlendMode: "difference" }}
                      />
                      {/* Forensic watermark — small bottom-right pulse every 30s for 3s */}
                      <ForensicWatermark
                        email={user?.email}
                        phone={userPhone}
                        fallback={user?.id?.slice(0, 8)}
                        enabled={!!blobVideoUrl && (securitySettings as any)?.watermark_enabled !== false}
                        flashEnabled={(securitySettings as any)?.watermark_flash_enabled !== false}
                        flashIntervalSeconds={Number((securitySettings as any)?.watermark_flash_interval_seconds) || 25}
                        cornerTopLeftIntervalSeconds={Number((securitySettings as any)?.watermark_corner_tl_interval_seconds ?? 17)}
                        cornerBottomRightIntervalSeconds={Number((securitySettings as any)?.watermark_corner_br_interval_seconds ?? 23)}
                        identity={((securitySettings as any)?.watermark_identity as never) || "both"}
                      />
                      {/* Custom fullscreen button — fullscreens the container so the watermark stays visible */}
                      {blobVideoUrl && <FullscreenButton targetRef={videoContainerRef as React.RefObject<HTMLElement>} />}
                    </div>
                  </Card>
                  {!videoCompleted && (
                    <p className="text-sm text-muted-foreground flex items-center gap-2">
                      <PlayCircle className="w-4 h-4" /> {t.watchVideo}
                    </p>
                  )}
                  {videoCompleted && questions.length > 0 && !progressMap.get(activeLesson.id)?.exam_passed && (
                    <Button size="lg" onClick={() => setShowExam(true)}>
                      <ClipboardList className="w-4 h-4 mr-2" /> {t.takeExam}
                    </Button>
                  )}
                  {progressMap.get(activeLesson.id)?.exam_passed && (
                    <div className="flex items-center gap-2 text-green-600">
                      <CheckCircle className="w-5 h-5" />
                      <span className="font-medium">{t.lessonCompleted} {t.score}: {progressMap.get(activeLesson.id)?.best_score}%</span>
                    </div>
                  )}
                </div>
              )}

              {!activeLesson.video_url && !showExam && questions.length > 0 && (
                <Button size="lg" onClick={() => { setVideoCompleted(true); setShowExam(true); }}>
                  <ClipboardList className="w-4 h-4 mr-2" /> {t.takeExam}
                </Button>
              )}

              {showExam && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2"><ClipboardList className="w-5 h-5" /> {t.lessonExam}</CardTitle>
                    <p className="text-sm text-muted-foreground">{t.passingScore}: {activeLesson.passing_score}% • {questions.length} {t.questions}</p>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {examResult ? (
                      <div className="space-y-4">
                        <div className={`text-center p-6 rounded-lg ${examResult.passed ? "bg-green-50 border border-green-200" : "bg-destructive/5 border border-destructive/20"}`}>
                          <p className="text-3xl font-bold mb-2">{examResult.score}%</p>
                          <p className={`font-medium ${examResult.passed ? "text-green-700" : "text-destructive"}`}>
                            {examResult.passed ? t.youPassed : `${t.passingScore}: ${activeLesson.passing_score}%`}
                          </p>
                        </div>

                        {/* Show correct answers only when passed */}
                        {examResult.passed && (
                          <div className="space-y-3 border-t border-border pt-4">
                            <p className="font-semibold text-sm text-muted-foreground uppercase tracking-wider">{t.correctAnswers}</p>
                            {questions.map((q, qi) => {
                              const userAnswer = parseInt(answers[q.id] || "-1");
                              const isCorrect = userAnswer === q.correct_option_index;
                              return (
                                <div key={q.id} className="space-y-1">
                                  <p className="font-medium text-sm">{qi + 1}. {getQText(q)}</p>
                                  <div className="flex flex-col gap-1 ml-4">
                                    {getQOpts(q).map((opt: string, oi: number) => {
                                      const isUserPick = oi === userAnswer;
                                      const isCorrectOpt = oi === q.correct_option_index;
                                      return (
                                        <p key={oi} className={`text-sm px-2 py-1 rounded ${
                                          isCorrectOpt ? "bg-green-50 text-green-700 font-medium border border-green-200" :
                                          isUserPick && !isCorrect ? "bg-destructive/5 text-destructive line-through border border-destructive/20" :
                                          "text-muted-foreground"
                                        }`}>
                                          {String.fromCharCode(65 + oi)}. {opt}
                                          {isCorrectOpt && " ✓"}
                                          {isUserPick && !isCorrect && " ✗"}
                                        </p>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {!examResult.passed && (
                          <Button className="w-full" onClick={() => { setExamResult(null); setAnswers({}); }}>
                            {t.tryAgain}
                          </Button>
                        )}
                        {examResult.passed && (
                          <Button className="w-full" onClick={() => {
                            const ci = lessons.findIndex((l) => l.id === activeLesson.id);
                            if (ci < lessons.length - 1) setActiveLesson(lessons[ci + 1]);
                          }}>
                            {t.nextLesson} <ChevronRight className="w-4 h-4 ml-1" />
                          </Button>
                        )}
                      </div>
                    ) : (
                      <>
                        {questions.map((q, qi) => (
                          <div key={q.id} className="space-y-3">
                            <p className="font-medium">{qi + 1}. {getQText(q)}</p>
                            <RadioGroup value={answers[q.id] || ""} onValueChange={(val) => setAnswers((prev) => ({ ...prev, [q.id]: val }))}>
                              {getQOpts(q).map((opt: string, oi: number) => (
                                <div key={oi} className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-muted/50 transition-colors">
                                  <RadioGroupItem value={String(oi)} id={`${q.id}-${oi}`} />
                                  <Label htmlFor={`${q.id}-${oi}`} className="flex-1 cursor-pointer">{opt}</Label>
                                </div>
                              ))}
                            </RadioGroup>
                          </div>
                        ))}
                        <div className="flex gap-3">
                          <Button variant="outline" onClick={() => setShowExam(false)}>{t.backToVideo}</Button>
                          <Button onClick={handleSubmitExam} disabled={submittingExam || Object.keys(answers).length < questions.length}>
                            {submittingExam && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} {t.submitExam}
                          </Button>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>
              )}

              {questions.length === 0 && !activeLesson.video_url && (
                <Card><CardContent className="py-8 text-center text-muted-foreground">{t.noContent}</CardContent></Card>
              )}

              {activeLesson && user?.id && (
                <LessonExtras
                  lessonId={activeLesson.id}
                  userId={user.id}
                  userName={user.user_metadata?.full_name || "Student"}
                />
              )}

              {(() => {
                const allPassed = lessons.length > 0 && lessons.every((l) => progressMap.get(l.id)?.exam_passed);
                if (!allPassed) return null;
                const fullName = user?.user_metadata?.full_name || "Student";
                return (
                  <Card className="border-primary/30 bg-primary/5">
                    <CardContent className="py-8 text-center space-y-4">
                      <Award className="w-16 h-16 mx-auto text-primary" />
                      <h2 className="text-2xl font-display font-bold">{t.courseComplete}</h2>
                      <p className="text-muted-foreground">
                        {t.congratulations}, <strong>{fullName}</strong>! {t.completedAll}
                      </p>
                      {certStatus === "approved" ? (
                        <p className="text-primary font-semibold">{t.certificateApproved}</p>
                      ) : certStatus === "pending" ? (
                        <p className="text-amber-600 font-semibold">{t.certificatePending}</p>
                      ) : certStatus === "rejected" ? (
                        <p className="text-destructive font-semibold">{t.certRejected}</p>
                      ) : (
                        <Button size="lg" disabled={requestingCert} onClick={handleRequestCertificate}>
                          {requestingCert && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                          <Award className="w-4 h-4 mr-2" /> {t.requestCertificate}
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                );
              })()}
            </div>
          ) : (
            <div className="text-center py-16 text-muted-foreground">{t.selectLesson}</div>
          )}
        </main>
      </div>
    </div>
  );
};

export default CourseView;
